import React, { FunctionComponent, useCallback, useEffect } from 'react';
import { makeStyles, Select, InputLabel, FormControl, MenuItem, Card, Grid } from '@material-ui/core';
import AuditHistoryNode from './AuditHistoryNode';
import { Application, AuditEventList, AuditEvent } from '../../../../store/modules/application/types';
import { LICENSE_HISTORY_CATEGORY_LIST } from '../../../../store/app.constants';
import TableEmptyState from '../../../../components/TableView/TableEmptyState';
import { getFullAddress, displayCurrencyAmount } from '../../../../utils/utils';
import _ from 'lodash';
import InfiniteScrollHistoryView from '../../../../components/InfiniteScrollHistoryView';
import { Skeleton } from '@material-ui/lab';

type AuditHistoryProps = {
    application: Application;
    auditHistoryList: AuditEventList;
    loading: boolean;
    hasMore: boolean;
    skip: number;
    setSkip: Function;
    setSelectedCategory: Function;
    selectedCategory: string;
    phrases: PhraseArrayType;
    setPhrases: Function;
};

type PhraseArrayType = { phrase: string[]; date: string; id: string }[];

type HistoryLogsType = {
    phraseArray: PhraseArrayType;
    hasMore: boolean;
    loadMore: Function;
    loading: boolean;
};

const AuditHistory: FunctionComponent<AuditHistoryProps> = (props) => {
    const {
        application,
        auditHistoryList,
        hasMore,
        loading,
        skip,
        setSkip,
        selectedCategory,
        setSelectedCategory,
        phrases,
        setPhrases,
    } = props;

    type keyValueRecord = {
        parentField: string | null;
        field: string;
        fieldName: string;
        updatedValue: any;
    };

    const classes = makeStyles(() => ({
        formControl: {
            marginLeft: 15,
            marginBottom: 15,
            minWidth: 160,
        },
        outermostContainer: {
            padding: 5,
            height: '100%',
            overflow: 'hidden',
        },
        paper: {
            width: '100%',
            height: '100%',
            overflow: 'hidden',
        },
        container: {
            height: '100%',
            width: '100%',
        },
        gridContainer: {
            height: '100%',
            width: '100%',
            display: 'grid',
            // gridTemplateRows: 'auto 1fr',
            padding: '0px',
        },
    }))();

    const minPhrasesToDisplay = 20;

    const handleSelectionChange = useCallback(
        (event: any) => {
            setPhrases([]);
            setSkip(0);
            setSelectedCategory(event.target.value);
        },
        [setSkip, setPhrases, setSelectedCategory],
    );

    const loadMore = () => {
        if (!loading && hasMore) {
            setSkip(skip + 1);
        }
    };

    const getPhrase = useCallback(
        (auditEventNode: AuditEvent) => {
            const { oldData, newData } = auditEventNode.details;
            const entityType = auditEventNode.entityType;
            const user = auditEventNode.user?.name;
            const getFieldName = (field: string) => _.startCase(field);
            const keyValueArray: keyValueRecord[] = [];
            const deletedProperties: string[] = [];
            const traverse = (objectTree: any, field: string, parentField: string, path?: string) => {
                if (typeof objectTree === 'object' && !!objectTree) {
                    for (const key in objectTree) {
                        if (parentField === 'person' || parentField === 'additionalData')
                            traverse(
                                objectTree[key],
                                key,
                                parentField,
                                `${key.match(/line[0-3]/y) ? 'Address - ' : ''}${getFieldName(key)}`,
                            );
                        else if (parentField === 'type' && key === 'name')
                            traverse(objectTree[key], key, parentField, 'Type');
                        else
                            traverse(
                                objectTree[key],
                                key,
                                parentField,
                                `${path ? path : getFieldName(parentField)} - ${getFieldName(key)}`,
                            );
                    }
                } else {
                    !!objectTree &&
                        field !== 'id' &&
                        keyValueArray.push({
                            parentField,
                            field,
                            fieldName: path || getFieldName(field),
                            updatedValue: objectTree,
                        });
                    return;
                }
            };

            const getDeletedProperties = (newDataTree: any, oldDataTree: any, path?: string) => {
                if (
                    typeof newDataTree === 'object' &&
                    typeof oldDataTree === 'object' &&
                    !!newDataTree &&
                    !!oldDataTree
                ) {
                    const newDataKeys: string[] = Object.keys(newDataTree);
                    const oldDataKeys: string[] = Object.keys(oldDataTree);
                    oldDataKeys.forEach((key) => {
                        if (!['documents'].includes(key))
                            if (!newDataKeys.includes(key)) deletedProperties.push(path ? path : getFieldName(key));
                            else if (['additionalData', 'person', 'address'].includes(key))
                                getDeletedProperties(newDataTree[key], oldDataTree[key]);
                            else
                                getDeletedProperties(
                                    newDataTree[key],
                                    oldDataTree[key],
                                    path
                                        ? `${path} - ${getFieldName(key)}`
                                        : `${key.match(/line[0-3]/y) ? 'Address - ' : ''}${getFieldName(key)}`,
                                );
                    });
                } else {
                    if (!newDataTree) path && deletedProperties.push(path);
                }
            };
            if (oldData && newData) {
                // Existing record was updated
                let phrase = [];
                switch (entityType) {
                    case 'License':
                        if (selectedCategory === 'Files' || selectedCategory === 'All') {
                            if (
                                newData.documents &&
                                ((oldData.documents && oldData.documents.length < newData.documents.length) ||
                                    !oldData.documents)
                            ) {
                                // new document(s) were added::
                                newData.documents.forEach((document: any) => {
                                    if (document) {
                                        phrase.push(
                                            `${user} uploaded file '${document.fileName}' of type ${document.metadata.documenttype}`,
                                        );
                                    }
                                });
                            } else if (oldData.documents) {
                                // document(s) were deleted::
                                let deletedFiles: string[] = [];
                                if (!newData.documents) {
                                    deletedFiles = oldData.documents.filter((document: any) => document);
                                } else if (newData.documents && oldData.documents.length > newData.documents.length) {
                                    deletedFiles = oldData.documents.filter((document: any) => {
                                        if (!document) return false;
                                        for (const newDocument of newData.documents)
                                            if (newDocument && document.id === newDocument.id) return false;
                                        return true;
                                    });
                                }
                                deletedFiles.forEach((file: any) => {
                                    phrase.push(`${user} deleted file '${file.fileName}'`);
                                });
                            }
                        }
                        if (selectedCategory === 'License' || selectedCategory === 'All') {
                            for (const field in newData) {
                                if (
                                    field === 'lastUpdatedOn' ||
                                    field === 'documents' ||
                                    field === 'history' ||
                                    field === 'lastUpdatedBy'
                                )
                                    continue;
                                if (typeof newData[field] === 'object' && !!newData[field]) {
                                    traverse(newData[field], field, field);
                                } else {
                                    !!newData[field] &&
                                        keyValueArray.push({
                                            parentField: null,
                                            field,
                                            fieldName: getFieldName(field),
                                            updatedValue: newData[field],
                                        });
                                }
                            }
                            keyValueArray.forEach((property: keyValueRecord) => {
                                if (!property.parentField && oldData[property.field]) {
                                    phrase.push(
                                        `${user} changed ${property.fieldName} from ${oldData[property.field]} to ${
                                            property.updatedValue
                                        }`,
                                    );
                                } else {
                                    phrase.push(`${user} changed ${property.fieldName} to ${property.updatedValue}`);
                                }
                            });
                            getDeletedProperties(newData, oldData);
                            deletedProperties.forEach((prop) => phrase.push(`${user} deleted ${prop} value`));
                        }

                        break;
                    case 'Violation':
                        for (const field in newData) {
                            if (
                                field === 'lastUpdatedOn' ||
                                field === 'id' ||
                                field === 'lastUpdatedBy' ||
                                field === 'description'
                            )
                                continue;
                            if (field === 'description') {
                                phrase.push(`${user} changed violation description to: '${newData[field]}'`);
                                continue;
                            }
                            if (typeof newData[field] === 'object' && !!newData[field]) {
                                traverse(newData[field], field, field);
                            } else {
                                !!newData[field] &&
                                    keyValueArray.push({
                                        parentField: null,
                                        field,
                                        fieldName: getFieldName(field),
                                        updatedValue: newData[field],
                                    });
                            }
                        }
                        keyValueArray.forEach((property: keyValueRecord) => {
                            //TODO: Revisit framing of the phrases for updating violation and inspection updates
                            if (!property.parentField && oldData[property.field]) {
                                phrase.push(
                                    `${user} changed Violation ${property.fieldName} from ${
                                        oldData[property.field]
                                    } to ${property.updatedValue}`,
                                );
                            } else {
                                phrase.push(
                                    `${user} changed Violation ${property.fieldName} to ${property.updatedValue}`,
                                );
                            }
                        });
                        getDeletedProperties(newData, oldData);
                        deletedProperties.forEach((prop) => phrase.push(`${user} deleted ${prop} value`));
                        break;
                    case 'Citation':
                        if (
                            newData.documents &&
                            ((oldData.documents && oldData.documents.length < newData.documents.length) ||
                                !oldData.documents)
                        ) {
                            // new document(s) were added::
                            newData.documents.forEach((document: any) => {
                                if (document) {
                                    phrase.push(
                                        `${user} uploaded file '${document.fileName}' of type ${document.metadata.documenttype}`,
                                    );
                                }
                            });
                        } else if (oldData.documents) {
                            // document(s) were deleted::
                            let deletedFiles: string[] = [];
                            if (!newData.documents) {
                                deletedFiles = oldData.documents.filter((document: any) => document);
                            } else if (newData.documents && oldData.documents.length > newData.documents.length) {
                                deletedFiles = oldData.documents.filter((document: any) => {
                                    if (!document) return false;
                                    for (const newDocument of newData.documents)
                                        if (newDocument && document.id === newDocument.id) return false;
                                    return true;
                                });
                            }
                            deletedFiles.forEach((file: any) => {
                                phrase.push(`${user} deleted file '${file.fileName}'`);
                            });
                        }
                        for (const field in newData) {
                            if (['lastUpdatedOn', 'lastUpdatedBy', 'id', 'licenses', 'documents'].includes(field))
                                continue;
                            if (field === 'monetaryPenalty' && !!newData.monetaryPenalty) {
                                const { invoiceType, billTo, fees } = newData.monetaryPenalty;
                                if (!oldData.monetaryPenalty) {
                                    phrase.push(`${user} generated a Citation Invoice`);
                                    invoiceType?.name && phrase.push(`${user} set Invoice Type to ${invoiceType.name}`);
                                }
                                billTo?.name &&
                                    billTo.name.length > 0 &&
                                    phrase.push(`${user} set Invoice Recipient to ${billTo.name}`);
                                billTo?.address &&
                                    phrase.push(`${user} set Recipient Address to ${getFullAddress(billTo.address)}`);
                                billTo?.email &&
                                    billTo.email.length > 0 &&
                                    phrase.push(`${user} set Recipient Email to ${billTo.email}`);
                                fees?.forEach((fee: any, index: number) => {
                                    if (!!fee) {
                                        if (!!oldData.monetaryPenalty?.fees[index]) {
                                            //change in existing fees
                                            ['feeType', 'amount', 'description'].forEach((field) => {
                                                const oldValue =
                                                    field === 'feeType'
                                                        ? oldData.monetaryPenalty.fees[index][field]?.name
                                                        : oldData.monetaryPenalty.fees[index][field];
                                                const newValue = field === 'feeType' ? fee[field]?.name : fee[field];
                                                if (!!oldValue) {
                                                    if (newValue) {
                                                        field === 'description'
                                                            ? phrase.push(
                                                                  `${user} changed description from '${oldValue}' to '${newValue}'`,
                                                              )
                                                            : phrase.push(
                                                                  `${user} changed ${getFieldName(field)} from ${
                                                                      field === 'amount' ? '$' : ''
                                                                  }${oldValue} to ${
                                                                      field === 'amount' ? '$' : ''
                                                                  }${newValue}`,
                                                              );
                                                    } else phrase.push(`${user} deleted ${getFieldName(field)} value`);
                                                } else if (fee[field]) {
                                                    field === 'description'
                                                        ? phrase.push(`${user} set fee description to '${newValue}'`)
                                                        : phrase.push(
                                                              `${user} set fee ${getFieldName(field)} to ${
                                                                  field === 'amount' ? '$' : ''
                                                              }${newValue}`,
                                                          );
                                                }
                                            });
                                        } else {
                                            //new fees added
                                            phrase.push(
                                                `${user} set fees of type ${fee.feeType.name} having amount $${fee.amount}`,
                                            );
                                        }
                                    }
                                });
                                continue;
                            }
                            if (typeof newData[field] === 'object' && !!newData[field]) {
                                traverse(newData[field], field, field);
                            } else {
                                !!newData[field] &&
                                    keyValueArray.push({
                                        parentField: null,
                                        field,
                                        fieldName: getFieldName(field),
                                        updatedValue: newData[field],
                                    });
                            }
                            getDeletedProperties(newData[field], oldData[field]);
                        }
                        keyValueArray.forEach((property: keyValueRecord) => {
                            if (!property.parentField && oldData[property.field])
                                phrase.push(
                                    `${user} changed Citation ${property.fieldName} from ${
                                        oldData[property.field]
                                    } to ${property.updatedValue}`,
                                );
                            else
                                phrase.push(
                                    `${user} changed Citation ${property.fieldName} to ${property.updatedValue}`,
                                );
                        });
                        deletedProperties.forEach((prop) => phrase.push(`${user} deleted ${prop} value`));
                        break;
                    case 'Inspection':
                        for (const field in newData) {
                            if (
                                field === 'lastUpdatedOn' ||
                                field === 'lastUpdatedBy' ||
                                field === 'id' ||
                                field === 'event'
                            )
                                continue;
                            if (typeof newData[field] === 'object' && !!newData[field]) {
                                traverse(newData[field], field, field);
                            } else {
                                !!newData[field] &&
                                    keyValueArray.push({
                                        parentField: null,
                                        field,
                                        fieldName: getFieldName(field),
                                        updatedValue: newData[field],
                                    });
                            }
                        }
                        keyValueArray.forEach((property: keyValueRecord) => {
                            if (!property.parentField && oldData[property.field])
                                phrase.push(
                                    `${user} changed ${property.fieldName} from ${oldData[property.field]} to ${
                                        property.updatedValue
                                    }`,
                                );
                            else phrase.push(`${user} changed ${property.fieldName} to ${property.updatedValue}`);
                        });
                        getDeletedProperties(newData, oldData);
                        deletedProperties.forEach((prop) => phrase.push(`${user} deleted ${prop} value`));
                        break;
                    case 'Comment':
                        //TODO: Revisit wording once edit capability is implemented
                        phrase.push(`${user} edited a comment`);
                        break;
                    case 'MergeQueueItem':
                        const mergeTemplate = auditEventNode.entityContext?.mergeTemplate?.name;
                        !!mergeTemplate && !!newData?.historyId && phrase.push(`${user} printed ${mergeTemplate}`);
                        break;
                }
                return phrase;
            } else if (oldData) {
                // Existing record was deleted
                let phrase = [];
                switch (entityType) {
                    case 'Violation':
                        phrase.push(`Violation '${oldData.category.name}' deleted by ${user}`);
                        break;
                    case 'Inspection':
                        phrase.push(`Inspection - ${oldData.inspectionDate} deleted by ${user}`);
                        break;
                    case 'Citation':
                        phrase.push(`Citation ${oldData.citationNumber} deleted by ${user}`);
                        break;
                    case 'Comment':
                        phrase.push(`${user} deleted a comment`);
                        break;
                }
                return phrase;
            } else {
                // New record was added OR entityType is MergeQueueItem
                let phrase = [];
                switch (entityType) {
                    case 'Violation':
                        phrase.push(`Violation added by ${user}`);
                        for (const field in newData) {
                            if (
                                field === 'lastUpdatedOn' ||
                                field === 'lastUpdatedBy' ||
                                field === 'description' ||
                                field === 'id' ||
                                field === 'license'
                            )
                                continue;
                            if (typeof newData[field] === 'object' && !!newData[field]) {
                                traverse(newData[field], field, field);
                            } else {
                                !!newData[field] &&
                                    keyValueArray.push({
                                        parentField: null,
                                        field,
                                        fieldName: getFieldName(field),
                                        updatedValue: newData[field],
                                    });
                            }
                        }
                        keyValueArray.forEach((property: keyValueRecord) => {
                            phrase.push(`${user} set ${property.fieldName} to ${property.updatedValue}`);
                        });
                        if (newData.description && newData.description.length > 0) {
                            phrase.push(`${user} set the description to: '${newData.description}'`);
                        }

                        break;
                    case 'Inspection':
                        phrase.push(`Inspection added by ${user}`);
                        for (const field in newData) {
                            if (
                                field === 'lastUpdatedOn' ||
                                field === 'lastUpdatedBy' ||
                                field === 'id' ||
                                field === 'license' ||
                                field === 'event'
                            )
                                continue;
                            if (typeof newData[field] === 'object' && !!newData[field]) {
                                traverse(newData[field], field, field);
                            } else {
                                !!newData[field] &&
                                    keyValueArray.push({
                                        parentField: null,
                                        field,
                                        fieldName: getFieldName(field),
                                        updatedValue: newData[field],
                                    });
                            }
                        }
                        keyValueArray.forEach((property: keyValueRecord) => {
                            phrase.push(`${user} set ${property.fieldName} to ${property.updatedValue}`);
                        });
                        break;
                    case 'License':
                        if (selectedCategory === 'Files') break;
                        newData.type && newData.person && phrase.push(`License added by ${user}`);
                        for (const field in newData) {
                            if (
                                field === 'lastUpdatedOn' ||
                                field === 'lastUpdatedBy' ||
                                field === 'id' ||
                                field === 'history'
                            )
                                continue;
                            if (typeof newData[field] === 'object' && !!newData[field]) {
                                traverse(newData[field], field, field);
                            } else {
                                !!newData[field] &&
                                    keyValueArray.push({
                                        parentField: null,
                                        field,
                                        fieldName: getFieldName(field),
                                        updatedValue: newData[field],
                                    });
                            }
                        }
                        keyValueArray.forEach((property: keyValueRecord) => {
                            phrase.push(`${user} set ${property.fieldName} to ${property.updatedValue}`);
                        });
                        break;
                    case 'Citation':
                        const getCitationLicenses = () => {
                            let licenseList = '';
                            newData.licenses.forEach((license: any, index: number) => {
                                if (index < newData.licenses.length - 1) licenseList += `${license.licenseNumber}, `;
                                else licenseList += `${license.licenseNumber}`;
                            });
                            return licenseList;
                        };
                        phrase.push(
                            `${user} added Citation ${newData.citationNumber} for license(s): ${getCitationLicenses()}`,
                        );
                        for (const field in newData) {
                            if (
                                [
                                    'lastUpdatedOn',
                                    'lastUpdatedBy',
                                    'id',
                                    'licenses',
                                    'person',
                                    'citationNumber',
                                    'citationDate',
                                    'createdOn',
                                    'createdBy',
                                ].includes(field)
                            )
                                continue;
                            if (typeof newData[field] === 'object' && !!newData[field]) {
                                traverse(newData[field], field, field);
                            } else {
                                !!newData[field] &&
                                    keyValueArray.push({
                                        parentField: null,
                                        field,
                                        fieldName: getFieldName(field),
                                        updatedValue: newData[field],
                                    });
                            }
                        }
                        keyValueArray.forEach((property: keyValueRecord) => {
                            phrase.push(`${user} set ${property.fieldName} to ${property.updatedValue}`);
                        });

                        break;
                    case 'MergeQueueItem':
                        const mergeTemplate = auditEventNode.entityContext?.mergeTemplate?.name;
                        !!mergeTemplate && !!newData?.historyId && phrase.push(`${user} printed ${mergeTemplate}`);
                        break;
                    case 'JournalEntry':
                        !!newData.lineItems &&
                            newData.lineItems.forEach((item: any) => {
                                const { type, amount, description, account, license } = item;
                                if (license?.id === application.id && account?.id !== '000000000000000000000001') {
                                    //TODO: Handle liability/equity accounts
                                    if (type === 'credit') {
                                        phrase.push(
                                            `${user} added a charge of ${displayCurrencyAmount(amount)} ${
                                                description?.length ? " with description '" + description + "'" : ''
                                            }`,
                                        );
                                    } else if (type === 'debit') {
                                        phrase.push(
                                            `${user} added a deposit of ${displayCurrencyAmount(amount)} ${
                                                description?.length ? " with description '" + description + "'" : ''
                                            }`,
                                        );
                                    }
                                }
                            });
                        break;
                    case 'Comment':
                        phrase.push(`${user} added a comment`);
                        break;
                }
                return phrase;
            }
        },
        [application.id, selectedCategory],
    );

    const updatePhrases = useCallback(
        (auditHistoryList: AuditEventList) => {
            if (!!auditHistoryList && !loading) {
                const phraseArray: PhraseArrayType = [];
                auditHistoryList.forEach((auditHistoryRecord) => {
                    const phrase = getPhrase(auditHistoryRecord);
                    phrase.length > 0 &&
                        phraseArray.push({ phrase, date: auditHistoryRecord.timestamp, id: auditHistoryRecord.id });
                });
                if (phraseArray.length < minPhrasesToDisplay && hasMore) {
                    setSkip(skip + 1);
                } else {
                    setPhrases(phraseArray);
                }
            }
        },
        // adding skip to dependency-list will create an infinite loop
        //eslint-disable-next-line
        [getPhrase, setPhrases, setSkip, loading, hasMore],
    );

    useEffect(() => {
        updatePhrases(auditHistoryList);
    }, [updatePhrases, auditHistoryList]);

    return (
        <div className={classes.outermostContainer}>
            <FormControl className={classes.formControl}>
                <InputLabel id="license-history-category-label">Category</InputLabel>
                <Select
                    labelId="license-history-category-label"
                    id="license-history-category"
                    defaultValue={LICENSE_HISTORY_CATEGORY_LIST[0].value}
                    value={selectedCategory}
                    onChange={handleSelectionChange}
                >
                    {LICENSE_HISTORY_CATEGORY_LIST.map((category) => (
                        <MenuItem key={category.id} value={category.value}>
                            {category.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Card className={classes.container}>
                {skip === 0 && loading ? (
                    <>
                        {[1, 2, 3].map((element) => (
                            <Grid key={element} container spacing={2} style={{ paddingLeft: 20 }}>
                                <Grid item sm={2}>
                                    <Skeleton />
                                </Grid>
                                <Grid item sm={5}>
                                    <Skeleton />
                                    <Skeleton />
                                    <Skeleton />
                                    <Skeleton />
                                </Grid>
                            </Grid>
                        ))}
                    </>
                ) : (
                    <HistoryLogs
                        phraseArray={phrases}
                        hasMore={!loading && hasMore}
                        loadMore={loadMore}
                        loading={loading}
                    />
                )}
            </Card>
        </div>
    );
};

const HistoryLogs: FunctionComponent<HistoryLogsType> = (props) => {
    const { phraseArray, hasMore, loadMore, loading } = props;
    if (phraseArray.length === 0 && !loading) {
        return (
            <TableEmptyState title="No history for this record" message="All history logs will be displayed here." />
        );
    } else {
        return (
            <InfiniteScrollHistoryView
                dataLength={phraseArray.length}
                hasMore={hasMore}
                loadMore={loadMore}
                loading={loading}
            >
                {phraseArray.map(({ id, date, phrase }) => (
                    <AuditHistoryNode key={id} date={date} description={phrase} />
                ))}
            </InfiniteScrollHistoryView>
        );
    }
};

export default AuditHistory;
